import { useState, useEffect, useCallback, useRef } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { router } from "@inertiajs/react"
import { Blend, Check, CircleCheck, CircleDashed, CirclePlay, Copy, Globe, Loader2, MailOpen, MailPlus, OctagonX, RefreshCw, Table } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataTableReact } from "@/components/ui/data-table-react"

import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet"
import { ScrollArea } from "@/components/ui/scroll-area"
import { DATA_TYPE_ITEMS } from "@/components/ui/plate-editor"
import { RequestPreview, substituteBodyTemplate } from "./RequestPreview"

const DATA_TYPE_ICON_MAP = DATA_TYPE_ITEMS.reduce((acc, item) => {
  acc[item.key] = item.icon
  return acc
}, {})

function statusMessage(agent, evaluating) {
  if (agent.analysis_status === "pending" || agent.analysis_status === "analyzing")
    return "Analyzing email…"
  if (evaluating || agent.active_trigger_match_status === "pending" || agent.active_trigger_match_status === "evaluating")
    return "Testing trigger…"
  if (agent.preview_extraction_status === "pending" || agent.preview_extraction_status === "extracting")
    return "Extracting data…"
  return null
}


export function SourceEmailSelector({ agent, workspace_slug }) {
  const [refreshing, setRefreshing] = useState(false)

  const sourceEmails = agent.source_emails || []

  // Track selection locally for immediate feedback, sync from server on prop change.
  // Only depend on active_source_email_id — not sourceEmails — to avoid resetting
  // the optimistic update on every render (sourceEmails is a new array reference each time).
  const [selectedId, setSelectedId] = useState(() => agent.active_source_email_id ?? sourceEmails[0]?.id)
  const sourceEmailIds = sourceEmails.map(se => se.id).join(",")

  useEffect(() => {
    setSelectedId(agent.active_source_email_id ?? sourceEmails[0]?.id)
  }, [agent.active_source_email_id, sourceEmailIds])

  const activeEmail = sourceEmails.find(se => se.id === selectedId) || sourceEmails[0]

  function handleSelectChange(sourceEmailId) {
    const numericId = Number(sourceEmailId)
    setSelectedId(numericId)

    router.patch(`/${workspace_slug}/agents/${agent.id}`, {
      agent: { active_source_email_id: sourceEmailId },
      redirect_to: "edit",
      silent: true
    }, { preserveScroll: true })
  }

  function handleRefresh() {
    setRefreshing(true)
    router.reload({
      only: ["agent"],
      onFinish: () => setRefreshing(false)
    })
  }

  if (sourceEmails.length === 0) return <AddTestEmailPrompt agent={agent} />

  return (
    <>
      <Select
        value={activeEmail ? String(activeEmail.id) : undefined}
        onValueChange={handleSelectChange}
      >
        <SelectTrigger id="test-email-select" className="w-52 rounded-r-none last:rounded-r-md relative hover:z-10 focus:z-10">
          <SelectValue placeholder="Select a preview email" />
        </SelectTrigger>
        <SelectContent position="popper" className="min-w-[var(--radix-select-trigger-width)] max-w-80">
          {sourceEmails.map((se) => (
            <SelectItem key={se.id} value={String(se.id)}>
              {se.subject || se.from_email || "Untitled email"}
            </SelectItem>
          ))}
          <SelectSeparator />
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="group/refresh w-full flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-accent rounded-sm transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 text-muted-foreground group-hover/refresh:text-foreground transition-colors ${refreshing ? "animate-spin" : ""}`} />
            <span>Refresh emails</span>
          </button>
          {agent.inbound_email_address && (
            <>
              <SelectSeparator className="mt-1" />
              <div className="px-2 py-1.5">
                <AddTestEmailInstructions agent={agent} />
              </div>
            </>
          )}
        </SelectContent>
      </Select>
      {activeEmail && <EmailPreviewDialog email={activeEmail} />}
    </>
  )
}

function AddTestEmailInstructions({ agent }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    if (!agent.inbound_email_address) return
    navigator.clipboard.writeText(agent.inbound_email_address).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [agent.inbound_email_address])

  if (!agent.inbound_email_address) return null

  return (
    <div className="text-xs text-muted-foreground flex items-center gap-x-1 gap-y-0.5 flex-wrap">
      <span>Add a preview email by emailing </span>
      <button
        type="button"
        onClick={handleCopy}
        className="group/copy inline-flex items-center gap-1 font-medium bg-muted px-1.5 py-0.5 rounded cursor-pointer transition-colors"
      >
        <span className="text-foreground break-all">{agent.inbound_email_address}</span>
        {copied ? (
          <Check className="h-3 w-3 text-green-500 shrink-0" />
        ) : (
          <Copy className="h-3 w-3 text-muted-foreground group-hover/copy:text-foreground shrink-0 transition-colors" />
        )}
      </button>
    </div>
  )
}

function AddTestEmailPrompt({ agent }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    if (!agent.inbound_email_address) return
    navigator.clipboard.writeText(agent.inbound_email_address).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [agent.inbound_email_address])

  if (!agent.inbound_email_address) return null

  return (
    <div className="h-full flex items-center justify-center">
      <div className="flex flex-col items-center text-center">
        <div className="rounded-full bg-muted p-4">
          <MailPlus className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-md font-medium mt-4 mb-2">Test your agent</p>
        <p className="text-sm text-muted-foreground mb-1">See how your agent will work by emailing</p>
        <button
          type="button"
          onClick={handleCopy}
          className="group/copy inline-flex items-center gap-1.5 text-xs font-mono bg-muted px-2.5 py-1.5 rounded-md cursor-pointer transition-colors"
        >
          <span className="text-foreground">{agent.inbound_email_address}</span>
          {copied ? (
            <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
          ) : (
            <Copy className="h-3.5 w-3.5 text-muted-foreground group-hover/copy:text-foreground shrink-0 transition-colors" />
          )}
        </button>
      </div>
    </div>
  )
}

function EmailPreviewDialog({ email }) {
  const [isOpen, setIsOpen] = useState(false)

  // Listen for Escape key from iframe
  useEffect(() => {
    if (!isOpen) return

    function handleMessage(e) {
      if (e.data?.type === 'emailEscape') {
        setIsOpen(false)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [isOpen])

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="group/preview h-9 w-9 p-0 rounded-none -ml-px relative hover:z-10 focus:z-10 last:rounded-r-md transition-colors"
            onClick={() => setIsOpen(true)}
          >
            <MailOpen className="h-3.5 w-3.5 text-muted-foreground group-hover/preview:text-foreground transition-colors" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>View email</TooltipContent>
      </Tooltip>
      <SheetContent showCloseButton={false} className="flex flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="gap-0.5 px-6 py-4 border-b shrink-0">
          <SheetTitle>{email.subject || "Untitled email"}</SheetTitle>
          <SheetDescription>
            From {email.from_email}
            {email.created_at && (
              <> on {new Date(email.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</>
            )}
          </SheetDescription>
        </SheetHeader>
        {email.body && (
          <div className="flex-1 min-h-0 overflow-hidden">
            <EmailPreview body={email.body} />
          </div>
        )}
        <SheetFooter className="flex-row justify-end border-t px-5 pt-[11px] pb-3">
          <Button variant="outline" size="sm" onClick={() => setIsOpen(false)}>Close</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

function EmailPreview({ body }) {
  // Inject CSS reset into the iframe
  const wrappedBody = `
    <style>
      html, body { margin: 0; padding: 0; overflow: visible; }
      #email-wrapper {
        padding: 16px;
        display: table;
      }
    </style>
    <div id="email-wrapper">${body}</div>
    <script>
      (function() {
        function measureAndReport() {
          var wrapper = document.getElementById('email-wrapper');
          if (!wrapper) return;
          window.parent.postMessage({
            type: 'emailSize',
            width: wrapper.offsetWidth,
            height: wrapper.offsetHeight
          }, '*');
        }
        measureAndReport();
        document.querySelectorAll('img').forEach(function(img) {
          if (!img.complete) img.onload = measureAndReport;
        });
        document.addEventListener('keydown', function(e) {
          if (e.key === 'Escape') {
            window.parent.postMessage({ type: 'emailEscape' }, '*');
          }
        });
      })();
    </script>
  `

  const [iframeSize, setIframeSize] = useState({ width: 600, height: 400 })

  useEffect(() => {
    function handleMessage(e) {
      if (e.data?.type === 'emailSize') {
        const { width, height } = e.data
        if (width > 0 && height > 0) {
          setIframeSize({
            width: Math.max(width, 200),
            height: Math.max(height, 50)
          })
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  function handleLoad(e) {
    const iframe = e.target
    try {
      const doc = iframe.contentDocument
      if (doc?.documentElement) doc.documentElement.scrollTop = 0
      if (doc?.body) doc.body.scrollTop = 0
    } catch {
      // Ignore cross-origin errors
    }
  }

  return (
    <ScrollArea className="h-full w-full" orientation="both">
      <div style={{ width: iframeSize.width, minWidth: '100%', height: iframeSize.height }}>
        <iframe
          srcDoc={wrappedBody}
          sandbox="allow-same-origin allow-scripts"
          scrolling="no"
          title="Email preview"
          className="bg-white border-0"
          style={{ width: "100%", height: "100%" }}
          onLoad={handleLoad}
        />
      </div>
    </ScrollArea>
  )
}

function parseReasonBullets(reason) {
  return reason
    .split("\n")
    .map(line => line.replace(/^(?:[•\-\*]|\d+\.)\s*/, "").trim().replace(/\.$/, ""))
    .filter(Boolean)
}

function renderInlineMarkdown(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/)
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    if (part.startsWith("*") && part.endsWith("*")) {
      return <em key={i}>{part.slice(1, -1)}</em>
    }
    return part
  })
}

function TriggerReasonTooltip({ matches, reason }) {
  const bullets = parseReasonBullets(reason)
  const headingColor = matches
    ? "text-green-600 dark:text-green-400"
    : "text-red-600 dark:text-red-400"
  const heading = matches ? "This email matches" : "This email doesn't match"

  return (
    <TooltipContent className="max-w-sm border border-input bg-background text-foreground shadow-md p-3">
      <div className={`text-sm font-medium mb-1 ${headingColor}`}>
        {heading}
      </div>
      <ul className="text-[13px] list-disc pl-5 space-y-1">
        {bullets.map((bullet, i) => (
          <li key={i}>{renderInlineMarkdown(bullet)}</li>
        ))}
      </ul>
    </TooltipContent>
  )
}

function TriggerMatchBadge({ evaluating, triggerMatch }) {
  if (!evaluating && triggerMatch?.matches === null) return null

  const isPending = evaluating || triggerMatch === null
  const key = isPending ? "pending" : triggerMatch.matches ? "pass" : "fail"

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={key}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
      >
        {isPending ? (
          <Badge variant="pending" size="medium">
            {evaluating ? <Loader2 className="animate-spin" /> : <CircleDashed />}
            Pending
          </Badge>
        ) : triggerMatch.matches ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="success" size="medium"><CircleCheck />Pass</Badge>
            </TooltipTrigger>
            {triggerMatch.reason && <TriggerReasonTooltip matches={true} reason={triggerMatch.reason} />}
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="destructive" size="medium"><OctagonX />Fail</Badge>
            </TooltipTrigger>
            {triggerMatch.reason && <TriggerReasonTooltip matches={false} reason={triggerMatch.reason} />}
          </Tooltip>
        )}
      </motion.div>
    </AnimatePresence>
  )
}

export function EditSidebar({ agent, workspace_slug, analysisInProgress, extractionInProgress, triggerMatchInProgress, runTestCount }) {
  // triggerMatch is DB-backed via props. runTestResult holds a local override after
  // clicking Run Test, so the result shows immediately without waiting for a page reload.
  const serverTriggerMatch = agent.trigger_match_by_email?.[agent.active_source_email_id] ?? null
  const [runTestResult, setRunTestResult] = useState(null)
  const [evaluating, setEvaluating] = useState(false)
  const lastRunTestCountRef = useRef(0)
  const prevEmailIdRef = useRef(agent.active_source_email_id)

  // Clear local Run Test result when the email selection changes
  useEffect(() => {
    if (agent.active_source_email_id === prevEmailIdRef.current) return
    prevEmailIdRef.current = agent.active_source_email_id
    setRunTestResult(null)
    setEvaluating(false)
  }, [agent.active_source_email_id])

  // Run Test: sync fetch that persists result to DB and updates local state immediately
  useEffect(() => {
    if (runTestCount === 0 || runTestCount <= lastRunTestCountRef.current) return
    if (!agent.active_source_email_id) return

    lastRunTestCountRef.current = runTestCount
    setEvaluating(true)
    setRunTestResult(null)

    const controller = new AbortController()

    fetch(`/${workspace_slug}/agents/${agent.id}/match_trigger`, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        "Accept": "application/json",
      },
      signal: controller.signal,
    })
      .then(r => r.json())
      .then(data => {
        setRunTestResult(data)
        setEvaluating(false)
      })
      .catch(err => { if (err.name !== "AbortError") setEvaluating(false) })

    return () => controller.abort()
  }, [runTestCount, agent.active_source_email_id, workspace_slug, agent.id])

  // Prefer local Run Test result over server result
  const triggerMatch = runTestResult ?? serverTriggerMatch

  return (
    <aside>

      {agent.source_emails?.length > 0 ? (
        <>
          <AnimatePresence initial={false}>
            {(analysisInProgress || extractionInProgress || triggerMatchInProgress || evaluating || (agent.analysis_status === "failed" && agent.analysis_error)) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="flex flex-col gap-2 pt-4 pb-2 px-5"
              >
                {(analysisInProgress || extractionInProgress || triggerMatchInProgress || evaluating) && (
                  <div className="flex gap-1.5 text-[13px] text-muted-foreground items-center overflow-hidden">
                    <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={statusMessage(agent, evaluating)}
                        initial={{ opacity: 0, y: 7 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -7 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="inline-block"
                      >
                        {statusMessage(agent, evaluating)}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                )}
                {agent.analysis_status === "failed" && agent.analysis_error && (
                  <div className="flex gap-1.5 text-[13px] text-destructive">
                    <OctagonX className="h-3 w-3 shrink-0 mt-0.75" />
                    <span>Analysis failed: {agent.analysis_error}</span>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence initial={false}>
            {!analysisInProgress && <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="flex flex-col"
            >

          <div data-size="test-trigger" className="py-4 px-6">
            <div className="text-base font-medium flex items-center gap-1.5 mb-1 h-7">
              <CirclePlay className="h-4 w-4 text-muted-foreground" />
              <span>Trigger</span>
            </div>
            <TriggerMatchBadge evaluating={evaluating || triggerMatchInProgress} triggerMatch={triggerMatch} />
          </div>

          <div data-size="test-data" className="py-4 px-6">
            <div className="text-base font-medium flex items-center gap-1.5 mb-1 h-7">
              <Table className="h-4 w-4 text-muted-foreground" />
              <span>Data</span>
              <AnimatePresence initial={false}>
                {extractionInProgress && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                    className="ml-auto"
                  >
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
            {agent.data_schema?.fields?.length > 0 ? (
              <>
                <DataTableReact
                    resizable
                    sortable
                    wrapCells
                    minColumnWidth={150}
                    maxColumnWidth={400}
                    columns={[
                      {
                        accessorKey: "key",
                        header: "Name",
                        cell: (value, row) => {
                          const dataTypeKey = typeof row?.data_type === "string" ? row.data_type.toLowerCase() : "text"
                          const Icon = DATA_TYPE_ICON_MAP[dataTypeKey] || Blend

                          return (
                            <span className="inline-flex gap-1.5">
                              <Icon className="size-3.5 mt-0.75 shrink-0 text-muted-foreground" />
                              {value}
                            </span>
                          )
                        },
                      },
                      { accessorKey: "value", header: "Value" },
                    ]}
                    data={agent.data_schema.fields.map((field) => {
                      const normalize = s => s?.toLowerCase().replace(/[\s_]+/g, " ").trim()
                      const extracted = agent.preview_extracted_data?.fields?.find(
                        f => normalize(f.name) === normalize(field.name)
                      )
                      return {
                        key: field.name,
                        data_type: field.data_type,
                        value: extracted?.value ?? null,
                      }
                    })}
                  />
                  {!extractionInProgress && agent.preview_extraction_status === "failed" && agent.preview_extraction_error && (
                    <div className="flex gap-1.5 text-[13px] text-destructive mt-2">
                      <OctagonX className="h-3 w-3 shrink-0 mt-0.75" />
                      <span>Extraction failed: {agent.preview_extraction_error}</span>
                    </div>
                  )}
                </>
            ) : (
              <p className="text-sm text-muted-foreground">No data fields defined</p>
            )}
          </div>

          <div data-size="test-request" className="py-4 px-6">
            <div className="text-base font-medium flex items-center gap-1.5 mb-1 h-7">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <span>Request</span>
              <AnimatePresence initial={false}>
                {extractionInProgress && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                    className="ml-auto"
                  >
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
            {agent.request_config?.url || agent.request_config?.body_template ? (
              <div>
                <RequestPreview
                  method={agent.request_config.method || "POST"}
                  url={agent.request_config.url}
                  headers={agent.request_config.headers || []}
                  body={substituteBodyTemplate(agent.request_config.body_template, agent.preview_extracted_data)}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No request configured</p>
            )}
          </div>

        </motion.div>}
          </AnimatePresence>
        </>
      ) : (
        <div className="flex flex-col gap-2 h-full">
          <AddTestEmailPrompt agent={agent} />
        </div>
      )}

    </aside>
  )
}
